import React, { useState } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  User,
  Banknote,
  Home,
  BookOpen,
  Code,
  Sparkles,
  Zap,
  Brain,
  ChevronDown,
  ExternalLink,
  GitCompare,
} from "lucide-react";
import NotificationsPanel from "./NotificationsPanel";
import "./COBLayout.css";

function COBLayout({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [apiDocsExpanded, setApiDocsExpanded] = useState(false);

  // Get API Docs URLs for COB pipeline
  const getApiDocsUrl = (version) => {
    const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      ? 'http://localhost:5001'
      : `${window.location.protocol}//${window.location.hostname}`;
    return `${baseUrl}/api/extraction/cob/${version}/docs`;
  };

  // API Docs items for expandable submenu
  const apiDocsItems = [
    { url: getApiDocsUrl('v1'), label: 'Subscription Checker V1', description: 'Gemini', color: '#4285f4', className: 'gemini' },
    { url: getApiDocsUrl('v2'), label: 'Subscription Checker V2', description: 'Qwen VL', color: '#059669', className: 'qwen' },
    { url: getApiDocsUrl('v3'), label: 'Change Of Bank', description: 'Bedrock', color: '#7c3aed', className: 'bedrock' },
  ];

  const menuItems = [
    { path: "/cob", icon: Home, label: "Dashboard", exact: true },
    { path: "/cob/dashboard", icon: Home, label: "Dashboard" },
    { type: "divider", label: "Subscription Checker" },
    { path: "/cob/v1-workorders", icon: Sparkles, label: "Subscription Checker V1", description: "Gemini", color: "#4285F4" },
    { path: "/cob/v2-workorders", icon: Zap, label: "Subscription Checker V2", description: "Qwen VL", color: "#059669" },
    { type: "divider", label: "Change Of Bank" },
    { path: "/cob/v3-workorders", icon: Brain, label: "Change Of Bank", description: "Bedrock", color: "#7c3aed" },
    { type: "divider", label: "Subscription Process" },
    { path: "/cob/v1-process-workorders", icon: GitCompare, label: "Subscription Process V1", description: "Gemini", color: "#7c3aed" },
    { path: "/cob/v2-process-workorders", icon: GitCompare, label: "Subscription Process V2", description: "Qwen VL", color: "#059669" },
    { type: "divider", label: "Management" },
    { path: "/cob/api-logs", icon: Code, label: "API Logs" },
  ];

  const isActive = (item) => {
    if (item.type === "divider") return false;
    if (item.exact) {
      return location.pathname === item.path;
    }
    if (item.path === "/cob/dashboard" && (location.pathname === "/cob" || location.pathname === "/cob/")) {
      return true;
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + "/");
  };

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    localStorage.removeItem("authToken");
    onLogout();
    navigate("/login");
  };

  const getPageTitle = () => {
    if (location.pathname === "/cob" || location.pathname === "/cob/" || location.pathname === "/cob/dashboard") {
      return "COB Dashboard";
    }
    if (location.pathname === "/cob/v1-workorders") {
      return "Subscription Checker V1 (Gemini)";
    }
    if (location.pathname === "/cob/v2-workorders") {
      return "Subscription Checker V2 (Qwen VL)";
    }
    if (location.pathname === "/cob/v3-workorders") {
      return "Change Of Bank (Bedrock)";
    }
    if (location.pathname === "/cob/extract/v1") {
      return "Extract V1 (Gemini)";
    }
    if (location.pathname === "/cob/extract/v2") {
      return "Extract V2 (Qwen VL)";
    }
    if (location.pathname === "/cob/extract") {
      return "Extract Cheques";
    }
    if (location.pathname === "/cob/v1-process-workorders") {
      return "Subscription Process V1 (Gemini)";
    }
    if (location.pathname === "/cob/v2-process-workorders") {
      return "Subscription Process V2 (Qwen VL)";
    }
    if (location.pathname.startsWith("/cob/process-work-order/")) {
      return "Process Work Order Details";
    }
    if (location.pathname.startsWith("/cob/work-order/")) {
      return "Work Order Details";
    }
    if (location.pathname === "/cob/api-logs") {
      return "API Logs";
    }
    const item = menuItems.find(m => m.type !== "divider" && isActive(m) && !m.exact);
    if (item) {
      return item.label;
    }
    return "COB Pipeline";
  };

  return (
    <div className="cob-layout">
      {/* Sidebar */}
      <aside className="cob-sidebar">
        <div className="cob-sidebar-header">
        {(user?.pipeline === "all" || user?.role === "superadmin" || user?.role === "admin") && (
          <button onClick={() => navigate("/dashboard")} style={{display:"flex",alignItems:"center",gap:6,padding:"8px 16px",margin:"0 12px 8px",background:"#f1f5f9",border:"1px solid #e2e8f0",borderRadius:6,cursor:"pointer",fontSize:12,color:"#475569",fontWeight:500}} title="Back to Main Dashboard">
            <span style={{fontSize:14}}>←</span> Main Dashboard
          </button>
        )}
          <div className="cob-logo">
            <Banknote size={24} className="cob-logo-icon" />
            <span className="cob-logo-text">COB Pipeline</span>
          </div>
          <div className="cob-logo-subtitle">Change of Bank</div>
        </div>

        <nav className="cob-sidebar-nav">
          {menuItems.filter(item => !item.exact).map((item, index) => (
            item.type === "divider" ? (
              <div key={`divider-${index}`} className="cob-nav-divider">
                <span>{item.label}</span>
              </div>
            ) : (
              <button
                key={item.path}
                className={`cob-nav-item ${isActive(item) ? "active" : ""}`}
                onClick={() => navigate(item.path)}
                title={item.label}
                style={item.color ? { "--item-color": item.color } : {}}
              >
                <item.icon size={20} style={item.color ? { color: item.color } : {}} />
                <div className="cob-nav-item-content">
                  <span className="cob-nav-label">{item.label}</span>
                  {item.description && <span className="cob-nav-description">{item.description}</span>}
                </div>
              </button>
            )
          ))}

          {/* API Docs with Expandable Submenu */}
          <div className="cob-nav-submenu-wrapper">
            <button
              className={`cob-nav-item cob-nav-item-external ${apiDocsExpanded ? 'expanded' : ''}`}
              onClick={() => setApiDocsExpanded(!apiDocsExpanded)}
              title="API Docs"
            >
              <BookOpen size={20} />
              <div className="cob-nav-item-content">
                <span className="cob-nav-label">API Docs</span>
                <span className="cob-nav-description">Swagger UI</span>
              </div>
              <ChevronDown size={16} className={`cob-nav-chevron ${apiDocsExpanded ? 'rotated' : ''}`} />
            </button>

            {/* Submenu Items */}
            <div className={`cob-nav-submenu ${apiDocsExpanded ? 'expanded' : ''}`}>
              {apiDocsItems.map((doc) => (
                <button
                  key={doc.url}
                  className={`cob-nav-submenu-item ${doc.className}`}
                  onClick={() => window.open(doc.url, "_blank")}
                  style={{ '--item-accent': doc.color }}
                >
                  <span className="cob-submenu-dot" style={{ background: doc.color }}></span>
                  <div className="cob-submenu-content">
                    <span className="cob-submenu-label">{doc.label}</span>
                    <span className="cob-submenu-desc">{doc.description}</span>
                  </div>
                  <ExternalLink size={14} className="cob-submenu-external-icon" />
                </button>
              ))}
            </div>
          </div>
        </nav>

        <div className="cob-sidebar-footer">
          <button
            className="cob-nav-item logout"
            onClick={handleLogout}
            title="Logout"
          >
            <LogOut size={20} />
            <div className="cob-nav-item-content">
              <span className="cob-nav-label">Logout</span>
            </div>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="cob-layout-content">
        {/* Top Header */}
        <header className="cob-top-header">
          <div className="cob-header-title">{getPageTitle()}</div>

          <div className="cob-header-actions">
            <NotificationsPanel />
            
            <div className="cob-user-menu">
              <button
                className="cob-user-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="cob-user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U'}
                </div>
                <span className="cob-user-name">{user?.name || user?.email}</span>
              </button>

              {showUserMenu && (
                <div className="cob-user-dropdown">
                  <div className="cob-dropdown-header">
                    <User size={18} />
                    <div>
                      <div className="cob-dropdown-name">{user?.name || 'COB User'}</div>
                      <div className="cob-dropdown-email">{user?.email}</div>
                    </div>
                  </div>
                  <hr />
                  <button
                    className="cob-dropdown-item logout"
                    onClick={handleLogout}
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="cob-page-content">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}

export default COBLayout;
